import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import {
  AlertTriangle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Download,
  HardDrive,
  ShieldCheck,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { apiClient, getErrorMessage } from "../../api/client";
import * as calendarioApi from "../../api/calendario";
import { SchedaCalendario, TipoScheda } from "../../api/calendario";
import * as scheduleApi from "../../api/schedule";
import { InstallationSchedule, Role } from "../../types";
import { SECTION_COLORS, SectionKey, sectionChipStyle, sectionTileStyle } from "../../utils/sectionColors";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { SlideOver } from "../../components/ui/SlideOver";
import { CalendarBand, CalendarView, DROP_GIORNO_PREFIX } from "../../components/ui/CalendarBand";
import { SchedaTrascinabile } from "../../components/ui/SchedaTrascinabile";
import { annunciTrascinamento, istruzioniTrascinamento } from "../../utils/dndAnnunci";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { InstallazioniPerStato } from "../../components/ui/InstallazioniPerStato";
import { etichettaIpotesiData, isMesePlaceholder } from "../../utils/dataIpotesi";

// Rotta della pagina che possiede il pannello di dettaglio di ciascun tipo.
// Clic su una riga -> si apre lì il pannello ESISTENTE (?open=id), con
// edit/sola-lettura secondo i permessi già in vigore su quella pagina/ruolo.
const ROUTE_FOR: Record<TipoScheda, string> = {
  installazione: "/schedule",
  sopralluogo: "/sopralluoghi",
  intervento: "/interventions",
  attivazione: "/activations",
};

// Colore del pallino nella fascia calendario — per TIPO (non per stato).
const TIPO_HEX: Record<TipoScheda, string> = {
  installazione: SECTION_COLORS.installazioni.base,
  sopralluogo: SECTION_COLORS.sopralluoghi.base,
  intervento: SECTION_COLORS.interventi.base,
  attivazione: SECTION_COLORS.attivazioni.base,
};

/**
 * Icona di STATO in fondo alla riga di un evento — si aggiunge al pallino di
 * TIPO senza toccarlo: il pallino dice DI CHE COSA si tratta (e i suoi colori
 * restano quelli di `SECTION_COLORS`, invariati), l'icona dice A CHE PUNTO è.
 *
 * Due soli colori: verde se svolta, rosso se no. Il caso "in ritardo" non ne
 * prende un terzo ma cambia il `title`, così chi si ferma sull'icona legge se
 * il rosso è "non ancora fatta" o "scaduta" — informazione che il badge
 * "In ritardo" dà già nelle quattro liste e che qui, dove i badge non ci sono,
 * andrebbe altrimenti persa.
 *
 * NESSUNA ICONA per ciò che è stato chiuso di proposito (installazione
 * `Rinviata`, attivazione `Cessata`): non è completato, ma non chiede niente a
 * nessuno, e il triangolo rosso lo farebbe sembrare un lavoro rimasto
 * indietro. L'assenza dice la cosa giusta senza aggiungere un terzo colore.
 * Attenzione: `in_ritardo` NON cambia per questo — una `Rinviata` scaduta
 * continua a contare come in ritardo nella sua lista e nella Dashboard, dove
 * la domanda è un'altra ("quanti lavori sono indietro", non "questo evento
 * chiede qualcosa").
 *
 * L'ordine conta: `completata` viene prima. Un'attivazione svolta e poi cessata
 * è stata fatta, e prende la spunta verde.
 *
 * Il verde e il rosso sono quelli dei badge `success` e `danger`: la stessa
 * attività non deve cambiare colore passando dal calendario alla sua lista.
 *
 * Non è decorativa: qui l'icona è l'UNICA forma in cui lo stato compare, quindi
 * porta un testo alternativo (sullo span, vedi sotto) invece di essere nascosta
 * agli screen reader.
 *
 * È una FUNZIONE che ritorna un nodo, non un componente. La differenza conta:
 * `<IconaStato />` sarebbe un elemento sempre "pieno" agli occhi di chi lo
 * riceve — il `null` uscirebbe solo dopo, quando React lo renderizza, e la
 * fascia avrebbe già creato l'involucro. Risultato: uno `span` vuoto con
 * `ml-auto` sulle righe che l'icona non ce l'hanno. Chiamandola qui, chi la
 * riceve vede davvero `null` e non avvolge niente.
 */
function iconaStato(scheda: SchedaCalendario): ReactNode {
  const completata = scheda.completata;
  if (!completata && scheda.chiusa_di_proposito) return null;
  const etichetta = completata ? "Completata" : scheda.in_ritardo ? "In ritardo" : "Non completata";
  const Icona = completata ? CheckCircle2 : AlertTriangle;
  // `title` e `aria-label` stanno sullo span e non sull'icona: le icone di
  // lucide non accettano `title` (non lo trasformano in <title> dell'SVG).
  return (
    <span role="img" aria-label={etichetta} title={etichetta} className="inline-flex">
      <Icona className={`h-3.5 w-3.5 ${completata ? "text-[#15803d]" : "text-[#dc2626]"}`} aria-hidden="true" />
    </span>
  );
}

// Export per-tipo (Fase 1.3, con colonna Stato) — riusati applicando lo stesso
// range di date del periodo selezionato nella fascia. Nessun export "misto".
const EXPORT_ENDPOINT: Record<TipoScheda, { endpoint: string; filename: string }> = {
  installazione: { endpoint: "/schedule/export", filename: "installazioni" },
  sopralluogo: { endpoint: "/sopralluoghi/export", filename: "sopralluoghi" },
  intervento: { endpoint: "/interventi/export", filename: "interventi" },
  attivazione: { endpoint: "/attivazioni/export", filename: "attivazioni" },
};

// Le quattro tessere di navigazione in cima alla pagina. Sostituiscono le voci
// di menu Sopralluoghi / Interventi / Installazioni / Attivazioni, ritirate
// dalla sidebar: il Calendario è la pagina dove quei quattro moduli si guardano
// insieme, quindi è da qui che ci si entra. Stesso aspetto delle tessere della
// Dashboard (`sectionTileStyle`), stessi colori di sezione.
const MODULI: {
  key: SectionKey;
  label: string;
  to: string;
  icon: typeof HardDrive;
  /** Lo stesso permesso che portava la voce di menu corrispondente: chi non
   *  vedeva la voce non vede la tessera. */
  permission: string;
  /** Ruoli ammessi dalla route guard, quando è più stretta del permesso. */
  allowedRoles?: Role[];
}[] = [
  { key: "sopralluoghi", label: "Sopralluoghi", to: "/sopralluoghi", icon: ClipboardCheck, permission: "sopralluoghi:read" },
  { key: "interventi", label: "Interventi", to: "/interventions", icon: ClipboardList, permission: "interventi:read" },
  {
    key: "installazioni",
    label: "Installazioni",
    to: "/schedule",
    icon: HardDrive,
    permission: "installazioni:read",
    // Il cliente HA `installazioni:read` — gli serve per vedere le proprie
    // installazioni qui dentro — ma /schedule gli è precluso: senza questa
    // lista la tessera lo manderebbe su una pagina che la route guard gli
    // nega. La lista ricalca gli `allowedRoles` della rotta in App.tsx, così
    // copre anche gli altri ruoli esclusi da quella pagina.
    allowedRoles: ["admin", "staff", "centro_assistenza", "operatore_tecnico"],
  },
  { key: "attivazioni", label: "Attivazioni", to: "/activations", icon: ShieldCheck, permission: "attivazioni:read" },
];

// Metadati per tipo: etichetta, icona, colore del badge. L'ordine è quello
// delle tessere qui sopra: filtri e tessere parlano degli stessi quattro
// moduli, e leggerli in due ordini diversi nella stessa pagina costringe a
// ricercare ogni volta la voce giusta.
const TIPI: {
  key: TipoScheda;
  label: string;
  icon: typeof HardDrive;
  color: "primary" | "accent" | "success" | "warning";
  /** Colore di sezione del chip di filtro — lo stesso della tessera e del
   *  pallino nella fascia, così i tre parlano dello stesso modulo. */
  section: SectionKey;
}[] = [
  { key: "sopralluogo", label: "Sopralluoghi", icon: ClipboardCheck, color: "accent", section: "sopralluoghi" },
  { key: "intervento", label: "Interventi", icon: ClipboardList, color: "warning", section: "interventi" },
  { key: "installazione", label: "Installazioni", icon: HardDrive, color: "primary", section: "installazioni" },
  { key: "attivazione", label: "Attivazioni", icon: ShieldCheck, color: "success", section: "attivazioni" },
];
const META = Object.fromEntries(TIPI.map((t) => [t.key, t]));

export default function CalendarioUnificato() {
  const { showToast } = useToast();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [schede, setSchede] = useState<SchedaCalendario[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Tutti i tipi attivi di default (checkbox).
  const [attivi, setAttivi] = useState<Set<TipoScheda>>(new Set(TIPI.map((t) => t.key)));

  // Dettaglio installazione in sola lettura: SOLO per il cliente, che non
  // accede a /schedule. Per tutti gli altri casi si naviga al pannello della
  // pagina del modulo (riuso, niente pannelli nuovi).
  const isCliente = user?.role === "cliente";

  // Tessere visibili: stesso permesso della voce di menu che sostituiscono,
  // più l'eventuale restrizione di ruolo della route guard. Non sono quattro
  // per tutti.
  const moduliVisibili = MODULI.filter(
    (m) => hasPermission(m.permission) && (!m.allowedRoles || (user != null && m.allowedRoles.includes(user.role)))
  );
  // Filtro per cliente (solo ruoli interni: il cliente è già ristretto alle sue aziende).
  const [filtroClienteId, setFiltroClienteId] = useState("");
  const [filtroClienteNome, setFiltroClienteNome] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<InstallationSchedule | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  function handleRowClick(s: SchedaCalendario) {
    if (s.tipo === "installazione" && isCliente) {
      setDetailOpen(true);
      setDetail(null);
      setDetailLoading(true);
      scheduleApi
        .getSchedule(s.id)
        .then(({ record }) => setDetail(record))
        .catch((err) => {
          setDetailOpen(false);
          showToast(getErrorMessage(err, "Impossibile aprire il dettaglio."), "error");
        })
        .finally(() => setDetailLoading(false));
      return;
    }
    navigate(`${ROUTE_FOR[s.tipo]}?open=${s.id}`);
  }

  // Contatori "Installazioni — per stato" per il CLIENTE: non ha la Dashboard
  // (HomeRoute lo manda qui), quindi la card vive nel Calendario. Stesso
  // componente della Dashboard e stesso endpoint GET /api/schedule/stats, già
  // scopizzato lato server con buildScopedWhere -> vede solo le sue installazioni.
  const [statsCliente, setStatsCliente] = useState<scheduleApi.ScheduleStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!isCliente) {
      setStatsLoading(false);
      return;
    }
    scheduleApi
      .getScheduleStats()
      .then(setStatsCliente)
      .catch(() => setStatsCliente(null))
      .finally(() => setStatsLoading(false));
  }, [isCliente]);

  // Periodo visibile nella fascia (per l'export e per la lista sotto il
  // calendario) + stato di export in corso.
  const [period, setPeriod] = useState<{ from: Date; to: Date; view: CalendarView } | null>(null);
  const [exportingTipo, setExportingTipo] = useState<TipoScheda | null>(null);

  async function exportTipo(tipo: TipoScheda) {
    if (!period) return;
    const { endpoint, filename } = EXPORT_ENDPOINT[tipo];
    const data_da = format(period.from, "yyyy-MM-dd");
    const data_a = format(period.to, "yyyy-MM-dd");
    setExportingTipo(tipo);
    try {
      const res = await apiClient.get(endpoint, { params: { data_da, data_a }, responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}_${data_da}_${data_a}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(getErrorMessage(err, "Export non riuscito."), "error");
    } finally {
      setExportingTipo(null);
    }
  }

  function ricarica() {
    setIsLoading(true);
    calendarioApi
      .getCalendario({ cliente_id: filtroClienteId || undefined })
      .then(setSchede)
      .catch((err) => showToast(getErrorMessage(err, "Impossibile caricare il calendario."), "error"))
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    ricarica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroClienteId]);

  function toggle(t: TipoScheda) {
    setAttivi((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  const visibili = schede.filter((s) => attivi.has(s.tipo));

  // La lista sotto il calendario segue il periodo navigato nella fascia invece
  // di mostrare sempre tutto: il calendario dà il colpo d'occhio, la lista i
  // dettagli dello stesso intervallo.
  //
  // Il confronto è su yyyy-MM-dd, la stessa chiave con cui CalendarBand
  // raggruppa i giorni: le date arrivano come @db.Date (mezzanotte UTC) e un
  // confronto fra oggetti Date locali sposterebbe di un giorno i record al
  // bordo del periodo.
  const chiaveGiorno = (d: string | Date) => format(new Date(d), "yyyy-MM-dd");

  /**
   * Una scheda che porta solo un MESE, non un giorno: `ipotesi_data` al primo
   * del mese è il segnaposto con cui si pianifica un'installazione a settembre
   * scrivendo 01/09, perché la colonna vuole un giorno (`utils/dataIpotesi.ts`).
   *
   * Il controllo su `campo_data` non è pignoleria: la regola vale solo per la
   * data IPOTIZZATA. Su una `data_effettiva`, o sulla `data_sopralluogo` di un
   * sopralluogo, il primo del mese è un giorno in cui qualcuno è andato davvero
   * sul posto, e travestirlo da mese cancellerebbe un fatto. È il backend a
   * dichiarare quale campo sta mostrando, quindi qui non si indovina.
   */
  const soloMese = (s: SchedaCalendario) => s.campo_data === "ipotesi_data" && isMesePlaceholder(s.data, s.stato);

  const nelPeriodo = period
    ? visibili.filter((s) => {
        if (!s.data || soloMese(s)) return false;
        const k = chiaveGiorno(s.data);
        return k >= chiaveGiorno(period.from) && k <= chiaveGiorno(period.to);
      })
    : visibili.filter((s) => s.data && !soloMese(s));

  // Le schede senza un giorno non appartengono a nessun periodo: restano in una
  // sezione a parte invece di sparire quando si filtra, altrimenti diventerebbero
  // invisibili proprio i record che più spesso richiedono un'azione.
  //
  // Ci finiscono anche i segnaposto di mese, che prima erano disegnati nella
  // fascia su un 1° settembre: una tessera dentro una cella-giorno afferma che
  // l'appuntamento è quel giorno, e per un segnaposto è falso. Qui la scheda
  // dichiara il proprio mese e resta trascinabile su un giorno vero.
  const senzaData = visibili.filter((s) => !s.data || soloMese(s));

  // --- Trascinamento di una scheda su un giorno ----------------------------
  // Vale per tutti e quattro i moduli. Il drop aggiorna SOLO la data, mai lo
  // stato: "Programmata" esiste nelle installazioni ma non nelle enumerazioni
  // di attivazioni (Attiva/Sospesa/Cessata) e sopralluoghi (Da revisionare/
  // Approvato/Da rifare), quindi far avanzare lo stato qui significherebbe
  // inventare una semantica diversa per ogni modulo.
  //
  // Unica eccezione, dentro `handleDragEnd`: un'installazione ancora "Da
  // programmare" trascinata sul giorno 1 del mese. Da `dataIpotesi.ts`, il
  // giorno 1 resta un segnaposto di mese finché lo stato non è "Programmata" —
  // non è un'invenzione qui, è la STESSA transizione che fa già il drop in
  // `pages/Schedule/Schedule.tsx`. Senza rifarla anche qui, trascinare una
  // scheda proprio sul 1° in questa pagina la farebbe sparire di nuovo nella
  // pila "Da programmare" invece di restare visibile nel giorno rilasciato.
  const [schedaTrascinata, setSchedaTrascinata] = useState<SchedaCalendario | null>(null);

  // Puntatore/touch con soglia (il click resta un click) + tastiera, che rende
  // il gesto raggiungibile senza mouse: Spazio avvia, le frecce spostano.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  // Vince il giorno sotto la punta del puntatore, non quello con più
  // sovrapposizione con l'anteprima: quella è larga quanto una scheda e
  // copriva più celle, facendo atterrare il record sul giorno sbagliato.
  // Il confronto fra rettangoli resta per la tastiera, che non ha puntatore.
  const collisionDetection: CollisionDetection = (args) => {
    const sottoIlPuntatore = pointerWithin(args);
    return sottoIlPuntatore.length > 0 ? sottoIlPuntatore : rectIntersection(args);
  };

  function handleDragStart(e: DragStartEvent) {
    const s = (e.active.data.current as { scheda?: SchedaCalendario } | undefined)?.scheda;
    setSchedaTrascinata(s ?? null);
  }

  async function handleDragEnd(e: DragEndEvent) {
    const scheda = schedaTrascinata;
    setSchedaTrascinata(null);
    if (!scheda || !e.over) return;

    const overId = String(e.over.id);
    if (!overId.startsWith(DROP_GIORNO_PREFIX)) return;
    const isoDate = overId.slice(DROP_GIORNO_PREFIX.length);

    // Vedi il commento sopra `schedaTrascinata`: un'installazione ancora "Da
    // programmare" passa a "Programmata" col drop, riusando la transizione già
    // fatta da Schedule.tsx.
    const extra =
      scheda.tipo === "installazione" && scheda.stato === "Da programmare"
        ? { stato: "Programmata" }
        : undefined;

    // "Nessuno spostamento" vale solo se anche lo stato resta com'è. Una
    // scheda che porta solo un segnaposto di mese (giorno 1) ha già
    // `ipotesi_data` sul 1° di quel mese: trascinarla PROPRIO sul 1° lascia il
    // valore identico, ma è il gesto che la rende un giorno vero. Saltare il
    // PATCH qui la terrebbe un segnaposto per sempre — è l'unico modo di
    // "confermare" esattamente il 1° del mese.
    const dataInvariata = scheda.data && format(new Date(scheda.data), "yyyy-MM-dd") === isoDate;
    if (dataInvariata && !extra) return;

    try {
      await calendarioApi.spostaSchedaSuData(scheda, isoDate, extra);
      showToast(
        `${scheda.titolo} spostata al ${format(new Date(isoDate), "d MMMM yyyy", { locale: it })}.`,
        "success"
      );
    } catch (err) {
      // Il backend resta l'autorità: can_edit evita di offrire il gesto dove
      // non serve, ma se un 403 arriva comunque la lista viene ricaricata e
      // nulla resta spostato solo a schermo.
      showToast(getErrorMessage(err, "Impossibile spostare la scheda."), "error");
    }
    ricarica();
  }

  const etichettaPeriodo = !period
    ? ""
    : period.view === "week"
      ? `${format(period.from, "d MMM", { locale: it })} – ${format(period.to, "d MMM yyyy", { locale: it })}`
      : period.view === "month"
        ? format(period.from, "MMMM yyyy", { locale: it })
        : format(period.from, "yyyy");

  const columns: TableColumn<SchedaCalendario>[] = [
    {
      key: "tipo",
      header: "Tipo",
      // Si ordina sull'etichetta, non sulla chiave: è quella che si legge in
      // tabella, e l'ordine deve corrispondere a quello che l'occhio si aspetta.
      sortValue: (s) => META[s.tipo].label,
      render: (s) => {
        const m = META[s.tipo];
        const Icon = m.icon;
        return (
          <Badge color={m.color}>
            <Icon className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
            {m.label.replace(/i$/, "")}
          </Badge>
        );
      },
    },
    { key: "titolo", header: "Titolo", sortValue: (s) => s.titolo, render: (s) => s.titolo },
    { key: "cliente", header: "Cliente", sortValue: (s) => s.cliente, render: (s) => s.cliente ?? "—" },
    {
      key: "data",
      header: "Data",
      // La stringa ISO si ordina già cronologicamente da sola: nessuna
      // conversione a Date, che qui non aggiungerebbe niente.
      //
      // In questa tabella `data` è sempre un giorno vero: `nelPeriodo` scarta i
      // segnaposto di mese, che vivono nella sezione senza data. Se un giorno
      // finissero qui, andrebbero ordinati sul mese e non sul 1° fittizio.
      sortValue: (s) => s.data,
      render: (s) => (s.data ? new Date(s.data).toLocaleDateString("it-IT") : "Senza data"),
    },
    {
      key: "stato",
      header: "Stato",
      sortValue: (s) => s.stato,
      render: (s) => (s.stato ? <Badge stato={s.stato}>{s.stato}</Badge> : "—"),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <CalendarDays className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-ink">Calendario</h1>
          <p className="mt-1 text-sm text-ink/50">Installazioni, sopralluoghi, interventi e attivazioni in un'unica vista.</p>
        </div>
      </header>

      {/* Contatori installazioni per il cliente (che non ha la Dashboard).
          Sola consultazione: non linkano a /schedule, pagina a lui preclusa. */}
      {isCliente && (
        <div className="mb-8">
          <InstallazioniPerStato
            byStato={statsCliente?.by_stato ?? {}}
            prossimi7Giorni={statsCliente?.prossimi_7_giorni ?? []}
            isLoading={statsLoading}
            linkToInstallazioni={false}
          />
        </div>
      )}

      {/* Filtro per cliente (ruoli interni) — vista aggregata "tutto di questo cliente" */}
      {!isCliente && (
        <div className="mb-4 max-w-sm">
          <ClienteAutocomplete
            label="Filtra per cliente"
            value={filtroClienteNome}
            onChange={(v) => {
              setFiltroClienteNome(v);
              if (v === "") setFiltroClienteId("");
            }}
            onSelect={(c) => {
              setFiltroClienteId(c.id);
              setFiltroClienteNome(c.nome);
            }}
          />
          {filtroClienteId && (
            <button
              type="button"
              onClick={() => {
                setFiltroClienteId("");
                setFiltroClienteNome("");
              }}
              className="mt-1 text-xs font-semibold text-primary hover:underline"
            >
              Rimuovi filtro cliente
            </button>
          )}
        </div>
      )}

      {/* Tessere di navigazione ai quattro moduli — hanno preso il posto delle
          voci di menu omonime, ritirate dalla sidebar. Stesso aspetto di quelle
          della Dashboard (`sectionTileStyle`). */}
      {moduliVisibili.length > 0 && (
        <div className="mb-6">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/40">Vai alla sezione</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {moduliVisibili.map(({ key, label, to, icon: Icon }) => (
              <Link
                key={key}
                to={to}
                style={sectionTileStyle(key)}
                className="flex flex-col items-center gap-2 rounded-card px-4 py-5 text-center transition-transform hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
              >
                <Icon className="h-6 w-6" aria-hidden="true" />
                <span className="text-sm font-semibold">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Filtro per tipo */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/40">Filtra nel calendario</p>
        <div className="flex flex-wrap gap-2">
          {TIPI.map((t) => {
            const on = attivi.has(t.key);
            const count = schede.filter((s) => s.tipo === t.key).length;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => toggle(t.key)}
                aria-pressed={on}
                style={sectionChipStyle(t.section, on)}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-shadow"
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {t.label}
                <span className="tabular-nums opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Export per periodo: riusa gli export per-tipo (Fase 1.3) applicati al
          range della fascia selezionato. Solo per i tipi attualmente attivi. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-ink/40">Esporta periodo</span>
        {TIPI.filter((t) => attivi.has(t.key)).map((t) => (
          <Button
            key={t.key}
            variant="secondary"
            size="sm"
            loading={exportingTipo === t.key}
            disabled={!period}
            onClick={() => exportTipo(t.key)}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            {t.label}
          </Button>
        ))}
      </div>

      {/* Fascia calendario + schede senza data, dentro lo stesso contesto di
          trascinamento: si prende una scheda dalla pila (o da un giorno) e la
          si lascia su un altro giorno. */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        accessibility={{ announcements: annunciTrascinamento, screenReaderInstructions: istruzioniTrascinamento }}
      >
        {/* Calendario e pila "Senza data" affiancati: il trascinamento richiede
            di vedere insieme origine e destinazione. Con la pila in fondo alla
            pagina i due non stavano mai nella stessa schermata — la stessa
            ragione per cui, sulle Installazioni, le tessere sono accanto alla
            fascia e non sopra. Sotto xl si impilano. */}
        <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-[1fr_320px] xl:items-start">
          <div className="min-w-0">
            <CalendarBand
              items={visibili}
              isLoading={isLoading}
              views={["week", "month", "year"]}
              droppable
              getCanEdit={(s) => s.can_edit}
              getMotivoBlocco={(s) => s.motivo_blocco}
              getId={(s) => `${s.tipo}:${s.id}`}
              // I segnaposto di mese non hanno un giorno in cui stare: `null`
              // li tiene fuori dalla fascia (contratto dichiarato da getDate) e
              // li lascia nella sezione qui sotto, che dichiara il mese.
              getDate={(s) => (soloMese(s) ? null : s.data)}
              getColor={(s) => TIPO_HEX[s.tipo]}
              getTitle={(s) => s.titolo}
              getSubtitle={(s) => s.cliente}
              // Già composto dal backend, uguale per tutti e quattro i tipi —
              // i sopralluoghi, che non hanno colonne orario, lo ricevono a null.
              getOrario={(s) => s.orario}
              // Il secondo simbolo della tessera: stato, a fine riga. Il
              // pallino di `getColor` qui sopra resta il tipo, com'era.
              renderStato={iconaStato}
              onItemClick={handleRowClick}
              onRangeChange={(from, to, view) => setPeriod({ from, to, view })}
            />
          </div>

          {/* Schede senza un giorno: non appartengono a nessun periodo, ma sono
              quelle che più spesso richiedono un'azione — si trascinano su un
              giorno per assegnare loro una data. Ci stanno sia quelle senza
              alcuna data sia i segnaposto di mese, che dichiarano il proprio
              mese sulla scheda. I sopralluoghi non compaiono mai qui
              (data_sopralluogo è NOT NULL e non è mai un segnaposto): per loro
              lo spostamento parte dalla tessera già nel calendario. */}
          {!isLoading && senzaData.length > 0 && (
            <section className="min-w-0">
              <h2 className="mb-3 flex items-baseline gap-x-2 text-sm font-bold uppercase tracking-wide text-ink/50">
                Da programmare
                <span className="tabular-nums font-semibold text-ink/40">({senzaData.length})</span>
              </h2>
              <div className="max-h-[560px] overflow-y-auto rounded-card border border-line bg-white px-3 shadow-card">
                {senzaData.map((s) => {
                  const m = META[s.tipo];
                  const Icon = m.icon;
                  const mese = soloMese(s) ? etichettaIpotesiData(s.data, s.stato) : null;
                  return (
                    <SchedaTrascinabile key={`${s.tipo}:${s.id}`} scheda={s} onApri={() => handleRowClick(s)}>
                      <div className="flex min-w-0 flex-col gap-1">
                        <Badge color={m.color}>
                          <Icon className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                          {m.label.replace(/i$/, "")}
                        </Badge>
                        <span className="truncate text-sm font-medium text-ink">{s.titolo}</span>
                        {s.cliente && <span className="truncate text-xs text-ink/50">{s.cliente}</span>}
                        {/* Il mese in cui va programmata. Senza, in questa
                            sezione un record da fare a settembre e uno di cui
                            non si sa nulla sarebbero indistinguibili. */}
                        {mese && (
                          <span className="flex items-center gap-1 text-xs font-medium text-primary">
                            <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            {mese}
                          </span>
                        )}
                      </div>
                    </SchedaTrascinabile>
                  );
                })}
              </div>
            </section>
          )}
        </div>

      {/* Elenco del periodo mostrato nella fascia */}
      <section className="mb-8">
        <h2 className="mb-3 flex flex-wrap items-baseline gap-x-2 text-sm font-bold uppercase tracking-wide text-ink/50">
          Elenco del periodo
          {etichettaPeriodo && <span className="font-semibold normal-case text-ink/40">· {etichettaPeriodo}</span>}
          {!isLoading && <span className="tabular-nums font-semibold text-ink/40">({nelPeriodo.length})</span>}
        </h2>
        <Table
          columns={columns}
          data={nelPeriodo}
          rowKey={(s) => `${s.tipo}:${s.id}`}
          isLoading={isLoading}
          emptyMessage="Nessuna scheda in questo periodo per i tipi selezionati."
          onRowClick={handleRowClick}
          ordinabile
        />
      </section>

        {/* Anteprima che segue il puntatore durante il trascinamento. */}
        <DragOverlay dropAnimation={null}>
          {schedaTrascinata && (
            <div className="w-[260px] rotate-1 cursor-grabbing rounded-card border border-primary bg-white p-3 shadow-lift">
              <p className="truncate text-sm font-semibold text-ink">{schedaTrascinata.titolo}</p>
              {schedaTrascinata.cliente && (
                <p className="truncate text-xs text-ink/50">{schedaTrascinata.cliente}</p>
              )}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Dettaglio installazione in sola lettura — solo per il cliente. */}
      <SlideOver open={detailOpen} onClose={() => setDetailOpen(false)} title="Dettaglio installazione">
        {detailLoading && <p className="text-sm text-ink/50">Caricamento…</p>}
        {detail && (
          <dl className="flex flex-col gap-3 text-sm">
            <DetailRow label="Insegna" value={detail.insegna} />
            <DetailRow label="Cliente" value={detail.cliente_nome} />
            <DetailRow label="Indirizzo" value={detail.indirizzo} />
            <DetailRow label="Località" value={[detail.localita, detail.provincia].filter(Boolean).join(" · ")} />
            <DetailRow label="Tipo dispositivo" value={detail.tipo_dispositivo} />
            <DetailRow label="Macchina" value={detail.macchina} />
            <DetailRow
              label="Dispositivo"
              value={[detail.dispositivo_marca, detail.dispositivo_modello, detail.dispositivo_matricola]
                .filter(Boolean)
                .join(" · ")}
            />
            <DetailRow label="Tecnico" value={detail.tecnico} />
            <DetailRow
              label="Data"
              value={
                detail.data_effettiva
                  ? new Date(detail.data_effettiva).toLocaleDateString("it-IT")
                  : detail.ipotesi_data
                    ? `${etichettaIpotesiData(detail.ipotesi_data, detail.stato)} (prevista)`
                    : "—"
              }
            />
            <div className="flex items-center justify-between gap-2">
              <dt className="text-ink/50">Stato</dt>
              <dd>{detail.stato ? <Badge stato={detail.stato}>{detail.stato}</Badge> : "—"}</dd>
            </div>
            <DetailRow label="Note" value={detail.note} />
          </dl>
        )}
      </SlideOver>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-ink/50">{label}</dt>
      <dd className="text-right font-medium text-ink">{value || "—"}</dd>
    </div>
  );
}
